import React, { useState } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity } from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { getDisplayName, getInitials } from '@/utils/residents/utils';

const TrashIcon = RESIDENTS_SVG_COMPONENTS.TRASH;

interface AutopayInfo {
  id: number;
  amount?: number;
  isFullAmount?: boolean;
  startDate?: string;
  endDate?: string;
  sourceId?: number;
}

interface AutopayUserCardProps {
  /** Lease ID */
  leaseId: number;
  /** User display info */
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  /** Property name */
  propertyName: string;
  /** Autopay data if exists */
  autopay?: AutopayInfo | null;
  /** Payment method label (e.g., "Visa *0101") */
  paymentMethodLabel?: string;
  /** Callback to setup autopay */
  onSetupAutopay: () => void;
  /** Callback to remove autopay - passes autopayId and displayName for toast message */
  onRemoveAutopay: (autopayId: number, displayName?: string) => Promise<void>;
  /** Callback when autopay is updated */
  onAutopayUpdated?: () => void;
  /** Whether setup is disabled (e.g., lease ended) */
  isSetupDisabled?: boolean;
}

/**
 * Autopay User Card Component
 * Displays user info with autopay status/details
 * Matches Figma designs for Autopay section in Account settings
 */
export const AutopayUserCard: React.FC<AutopayUserCardProps> = ({
  leaseId,
  firstName,
  lastName,
  photoUrl,
  propertyName,
  autopay,
  paymentMethodLabel = '—',
  onSetupAutopay,
  onRemoveAutopay,
  onAutopayUpdated,
  isSetupDisabled = false,
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);

  const hasAutopay = Boolean(autopay);
  
  // Use shared utility functions
  const displayName = getDisplayName(firstName, lastName) || 'Resident';
  const initials = getInitials(firstName, lastName) || 'R';

  // Format date for display
  const formatDate = (iso?: string): string => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  // Get processed day text
  const getProcessedText = (): string => {
    // Autopay processes on 1st of each month
    return '1st day of each month';
  };

  // Format amount text
  const amountText = autopay?.amount 
    ? `$${Number(autopay.amount).toFixed(2)}` 
    : (autopay?.isFullAmount !== false ? 'Full balance' : '—');

  // Show remove confirmation modal
  const handleRemove = () => {
    if (!autopay?.id) return;
    setShowRemoveConfirmModal(true);
  };

  // Handle cancel remove
  const handleRemoveCancel = () => {
    setShowRemoveConfirmModal(false);
  };

  // Handle confirm remove - Figma: "Yes, I'm sure" button
  // Passes displayName for the success toast message
  const handleRemoveConfirm = async () => {
    if (!autopay?.id) {
      setShowRemoveConfirmModal(false);
      return;
    }
    
    // Store the ID before closing modal to avoid stale closure issues
    const autopayId = autopay.id;
    const name = displayName;
    
    try {
      // Close modal first to give immediate UI feedback
      setShowRemoveConfirmModal(false);
      // Then call the remove API
      await onRemoveAutopay(autopayId, name);
    } catch (error) {
      // Error handled by parent (shows toast)
    }
  };

  const handleEditSettings = () => {
    setShowDetailsModal(true);
  };

  const handleModalClose = () => {
    setShowDetailsModal(false);
  };

  const handleAutopayUpdated = () => {
    onAutopayUpdated?.();
  };

  return (
    <>
      <View style={styles.container}>
        {/* Avatar */}
        <View style={styles.avatarContainer}>
          {photoUrl ? (
            <Image source={{ uri: photoUrl }} style={styles.avatarImage} />
          ) : (
            <Text style={styles.avatarInitials}>{initials}</Text>
          )}
        </View>

        {/* Info Section */}
        <View style={styles.infoContainer}>
          {/* Top: Name + Property */}
          <View style={styles.topSection}>
            <Text style={styles.userName}>{displayName}</Text>
            <Text style={styles.propertyName}>{propertyName}</Text>
          </View>

          {/* Bottom: Autopay details or Setup button */}
          {hasAutopay ? (
            <View style={styles.autopayDetails}>
              {/* Info rows */}
              <View style={styles.infoGrid}>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Autopay amount</Text>
                  <Text style={styles.infoValue}>{amountText}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Autopay start</Text>
                  <Text style={styles.infoValue}>{formatDate(autopay?.startDate)}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Autopay end</Text>
                  <Text style={styles.infoValue}>{formatDate(autopay?.endDate)}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Processed</Text>
                  <Text style={styles.infoValue}>{getProcessedText()}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Payment method</Text>
                  <Text style={styles.infoValue}>{paymentMethodLabel}</Text>
                </View>
              </View>

              {/* Actions */}
              <View style={styles.actionsRow}>
                <TouchableOpacity onPress={handleEditSettings} activeOpacity={0.7}>
                  <Text style={styles.editSettingsText}>Edit settings</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleRemove} style={styles.removeButton} activeOpacity={0.7}>
                  <TrashIcon width={20} height={20} color="#5E5E5E" />
                  <Text style={styles.removeText}>Remove</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <View style={styles.setupButtonContainer}>
              <TouchableOpacity 
                style={[styles.setupButton, isSetupDisabled && styles.setupButtonDisabled]} 
                onPress={onSetupAutopay}
                disabled={isSetupDisabled}
                activeOpacity={0.8}
              >
                <Text style={styles.setupButtonText}>Setup autopay</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>

      {/* Autopay Details Modal */}
      <MobileAutopayDetailsModal
        visible={showDetailsModal}
        onClose={handleModalClose}
        leaseId={leaseId}
        onAutopayUpdated={handleAutopayUpdated}
      />

      {/* Remove Autopay Confirmation Modal - Figma design 5. Remove autopay */}
      <ConfirmationModal
        visible={showRemoveConfirmModal}
        message={`Are you sure you want to remove autopay for ${displayName}?`}
        onCancel={handleRemoveCancel}
        onProceed={handleRemoveConfirm}
        cancelText="No, cancel"
        proceedText="Yes, I'm sure"
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 18,
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  // Avatar - Figma: 68x68, rounded-[34px], bg rgba(238,241,244,0.1)
  avatarContainer: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: 'rgba(238, 241, 244, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 68,
    height: 68,
  },
  // Figma: text-[24px], font-bold, text-[#eef1f4]
  avatarInitials: {
    fontSize: 24,
    fontWeight: '700',
    color: '#EEF1F4',
    lineHeight: 36,
  },
  // Info container
  infoContainer: {
    flex: 1,
    gap: 8,
  },
  topSection: {
    gap: 2,
  },
  // Figma: text-[18px], font-semibold, text-[#eef1f4]
  userName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#EEF1F4',
    lineHeight: 27,
  },
  // Figma: text-[14px], font-normal, text-[#eef1f4]
  propertyName: {
    fontSize: 14,
    fontWeight: '400',
    color: '#EEF1F4',
    lineHeight: 21,
  },
  // Autopay details section
  autopayDetails: {
    marginTop: 12,
  },
  infoGrid: {
    gap: 8,
  },
  infoRow: {
    flexDirection: 'row',
    gap: 24,
  },
  // Figma: text-[12px], font-light, text-[#eef1f4]
  infoLabel: {
    fontSize: 12,
    fontWeight: '300',
    color: '#EEF1F4',
    lineHeight: 18,
    minWidth: 100,
  },
  // Figma: text-[12px], font-light, text-[#acacac]
  infoValue: {
    fontSize: 12,
    fontWeight: '300',
    color: '#ACACAC',
    lineHeight: 18,
    flex: 1,
  },
  // Actions row
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    marginTop: 14,
  },
  // Figma: text-[12px], font-medium, text-[#90aafe]
  editSettingsText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#90AAFE',
    lineHeight: 18,
  },
  removeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  // Figma: text-[12px], font-medium, text-[#919191]
  removeText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#919191',
    lineHeight: 18,
  },
  // Setup button container
  setupButtonContainer: {
    marginTop: 12,
  },
  // Figma: bg-[#f55560], h-[34px], rounded-[8px], px-[16px] py-[8px]
  setupButton: {
    backgroundColor: '#F55560',
    height: 34,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
  },
  setupButtonDisabled: {
    opacity: 0.5,
  },
  // Figma: text-[12px], font-medium, text-neutral-900
  setupButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#171717',
    lineHeight: 18,
  },
});

export default AutopayUserCard;
